/* PrivacyPage — /privacy
 *
 * UK GDPR + UK DPA 2018 starting template. Reviewed by counsel: NO.
 * Update the named officer, registered address and ICO registration
 * number once they exist. Update Section 6 (retention) once Phase 2
 * TTL indexes ship — see /app/backend/routes/plans.py.
 */
import LegalLayout from '../../components/legal/LegalLayout';

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="8 February 2026" activePath="/privacy">
      <p>
        HyreDocs Ltd (&quot;<strong>HyreDocs</strong>&quot;, &quot;<strong>we</strong>&quot;, &quot;<strong>our</strong>&quot;) is a UK-based software platform that helps recruitment agencies collect, sign and store onboarding paperwork. This Privacy Policy explains what personal data we process, why, and what rights you have under the UK General Data Protection Regulation (&quot;UK GDPR&quot;) and the Data Protection Act 2018.
      </p>

      <h2>1. Who is the &quot;controller&quot; of your data?</h2>
      <p>
        HyreDocs operates a <strong>multi-tenant SaaS</strong>. Most of the time we act as a <strong>data processor</strong> on behalf of the recruitment agency that invited you to upload or sign a document. That agency is the <strong>controller</strong> and decides what data to collect, why, and for how long.
      </p>
      <p>
        We act as a <strong>controller</strong> only for:
      </p>
      <ul>
        <li>Account holders at recruitment agencies (their work email, name and login activity);</li>
        <li>Visitors to the HyreDocs marketing website (cookie and form-submission data);</li>
        <li>Anyone who contacts our sales or support teams.</li>
      </ul>

      <h2>2. What personal data we process</h2>
      <p>The categories of personal data we typically process are:</p>
      <ul>
        <li><strong>Account data</strong> — name, work email, password hash, role.</li>
        <li><strong>Candidate identification data</strong> — name, email, phone, address, date of birth, National Insurance number (only where the agency&apos;s template requires it).</li>
        <li><strong>Candidate document content</strong> — anything the candidate types into forms or PDF templates (right-to-work, DBS, IR35, P45, references, signed contracts).</li>
        <li><strong>Uploaded files</strong> — copies of identity documents, credentials, certifications.</li>
        <li><strong>Signature evidence</strong> — IP address, user agent, timestamp and SHA-256 hash of the signed document, captured on every signature event.</li>
        <li><strong>Bullhorn integration data</strong> — internal Bullhorn record IDs and a securely-encrypted OAuth refresh token where the agency has connected Bullhorn.</li>
        <li><strong>Technical data</strong> — IP address, browser type, pages visited, error logs.</li>
      </ul>
      <p>
        We do not knowingly process special-category data (e.g. health, biometric, criminal-record data) <strong>unless</strong> the agency&apos;s template specifically asks the candidate to upload it (for example, a DBS certificate). Where that happens we treat the data with elevated controls.
      </p>

      <h2>3. Why we process your data (lawful basis)</h2>
      <table>
        <thead>
          <tr><th>Purpose</th><th>Lawful basis (UK GDPR Article 6)</th></tr>
        </thead>
        <tbody>
          <tr><td>Providing the HyreDocs service to a paying agency</td><td>Contract performance (Art. 6(1)(b))</td></tr>
          <tr><td>Sending onboarding documents to a candidate on the agency&apos;s instruction</td><td>Processor acting under controller instruction (Art. 28)</td></tr>
          <tr><td>Securing the platform, preventing fraud and abuse</td><td>Legitimate interests (Art. 6(1)(f))</td></tr>
          <tr><td>Sales and marketing emails to recruitment agencies</td><td>Legitimate interests / consent (Art. 6(1)(a), (f))</td></tr>
          <tr><td>Compliance with HMRC, ICO or other statutory obligations</td><td>Legal obligation (Art. 6(1)(c))</td></tr>
        </tbody>
      </table>

      <h2>4. Who we share data with</h2>
      <p>
        We share personal data only with the sub-processors strictly necessary to run the service. The complete and current list is published at <a href="/legal/subprocessors">/legal/subprocessors</a>. We sign written contracts with each sub-processor requiring them to meet the same data-protection standards we owe you.
      </p>
      <p>
        We do not sell personal data. We do not share it for cross-context behavioural advertising.
      </p>

      <h2>5. International transfers</h2>
      <p>
        Some of our sub-processors are based outside the UK / EEA (for example, MongoDB Atlas, Amazon Web Services). Where personal data is transferred outside the UK we rely on the <strong>UK International Data Transfer Addendum</strong> to the EU Standard Contractual Clauses, plus, where appropriate, supplementary measures such as encryption-in-transit and encryption-at-rest.
      </p>

      <h2>6. How long we keep your data</h2>
      <p>
        We keep personal data only as long as necessary to provide the service or as required by law. Retention periods are set by the recruitment agency that controls the record. As of the date of this policy, our default retention schedule is:
      </p>
      <ul>
        <li><strong>Draft documents</strong> — automatically deleted after 60 days of inactivity.</li>
        <li><strong>Abandoned form submissions</strong> — automatically deleted after 90 days.</li>
        <li><strong>Signed documents and their audit certificates</strong> — kept for the agency&apos;s nominated retention period, typically 6 years to align with UK employment-record statutes.</li>
        <li><strong>System audit logs</strong> — kept for 7 years.</li>
        <li><strong>Account data</strong> — kept while the agency subscription is active, deleted within 90 days of cancellation.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>You have the following rights under UK GDPR. To exercise any of them, contact <a href="mailto:privacy@hyredocs.com">privacy@hyredocs.com</a> — we respond within one calendar month.</p>
      <ul>
        <li><strong>Access</strong> — get a copy of the personal data we hold about you.</li>
        <li><strong>Rectification</strong> — correct inaccurate or incomplete data.</li>
        <li><strong>Erasure</strong> — ask us to delete your data (subject to the agency&apos;s legal-hold obligations).</li>
        <li><strong>Restriction</strong> — pause processing while a dispute is resolved.</li>
        <li><strong>Portability</strong> — receive your data in a structured, machine-readable format.</li>
        <li><strong>Objection</strong> — object to processing based on legitimate interests.</li>
        <li><strong>Withdraw consent</strong> — at any time, where the lawful basis is consent.</li>
        <li><strong>Complain</strong> — to the UK Information Commissioner&apos;s Office at <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.</li>
      </ul>
      <p>
        <strong>If you are a candidate</strong> and the recruitment agency is the controller of your data, please contact the agency in the first instance. We will forward your request to the right agency within 5 working days.
      </p>

      <h2>8. Security</h2>
      <p>We protect personal data with measures including:</p>
      <ul>
        <li>TLS 1.2+ encryption in transit on every connection;</li>
        <li>Encryption at rest on the managed database tier;</li>
        <li>Application-level encryption for the most sensitive credentials (e.g. Bullhorn OAuth tokens) using AES-128 in GCM mode via Fernet;</li>
        <li>Password hashing with <code>bcrypt</code> and per-password salts;</li>
        <li>HTTP-only, secure cookies for session tokens;</li>
        <li>Strict tenant isolation — every record carries a <code>tenant_id</code> enforced at the API layer;</li>
        <li>Comprehensive audit logging of administrative actions;</li>
        <li>Regular dependency scanning and least-privilege access controls.</li>
      </ul>

      <h2>9. Cookies</h2>
      <p>
        We use a small number of strictly necessary cookies and, with your consent, optional analytics cookies. Full details are in our <a href="/cookies">Cookie Policy</a>.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We&apos;ll post material changes here and notify account holders by email. Continued use of HyreDocs after a change means you accept the updated policy.
      </p>

      <h2>11. Contact us</h2>
      <p>
        <strong>Data Protection contact:</strong> <a href="mailto:privacy@hyredocs.com">privacy@hyredocs.com</a><br />
        <strong>Postal address:</strong> HyreDocs Ltd, United Kingdom (full registered address provided on request)<br />
        <strong>UK ICO registration:</strong> in progress
      </p>
    </LegalLayout>
  );
}
